"use client";

import { Button, cn, Input, toast } from "@devhop/ui";
import {
  MinusIcon,
  PackageIcon,
  PlusIcon,
  ScanBarcodeIcon,
  Scissors,
} from "lucide-react";
import {
  useCallback,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { ShopFeatureFlags } from "@/modules/merchant/domain/contracts/shop-feature-flags";
import { PosCartLocationHint } from "@/modules/merchant/presentation/ui/PosCartLocationHint";
import { newPosLineInstanceId } from "@/modules/merchant/presentation/ui/ShopPosCheckoutModal";
import { formatPrice } from "@/shared/lib/currency";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import {
  lineDiscountCapped,
  lineGrossTotal,
  posCartLineKey,
  type ShopPosCartLineExt,
} from "./shop-pos-cart";

export type ShopPosCartPanelProps = {
  cart: ShopPosCartLineExt[];
  setCart: Dispatch<SetStateAction<ShopPosCartLineExt[]>>;
  simpleMode: boolean;
  lastScannedLineKey: string | null;
  shopId: string | undefined;
  branchId: string | null;
  featureFlags: ShopFeatureFlags | null | undefined;
  posSalesUnlocked: boolean;
};

export function ShopPosCartPanel({
  cart,
  setCart,
  simpleMode,
  lastScannedLineKey,
  shopId,
  branchId,
  featureFlags,
  posSalesUnlocked,
}: ShopPosCartPanelProps) {
  const [qtyEditLineKey, setQtyEditLineKey] = useState<string | null>(null);
  const lastQtyTapRef = useRef<{ key: string; t: number } | null>(null);

  const handleQtyTap = useCallback((lineKey: string) => {
    const now = Date.now();
    const prev = lastQtyTapRef.current;
    if (prev?.key === lineKey && now - prev.t < 450) {
      setQtyEditLineKey(lineKey);
      lastQtyTapRef.current = null;
      return;
    }
    lastQtyTapRef.current = { key: lineKey, t: now };
  }, []);

  const commitQtyEdit = useCallback(
    (lineKey: string, raw: string) => {
      setQtyEditLineKey(null);
      const n = raw === "" ? 0 : Number.parseInt(raw, 10);
      if (!Number.isFinite(n) || n < 1) return;
      setCart((prev) => {
        const line = prev.find((l) => posCartLineKey(l) === lineKey);
        if (!line || line.serialId) return prev;
        let cap = Number.POSITIVE_INFINITY;
        if (line.lotId != null && line.lotQtyCap != null) {
          const totalSameLot = prev
            .filter((l) => l.row.id === line.row.id && l.lotId === line.lotId)
            .reduce((s, l) => s + l.qty, 0);
          cap = line.lotQtyCap - (totalSameLot - line.qty);
        } else if (line.row.quantityOnHand !== null) {
          const totalPlain = prev
            .filter((l) => !l.lotId && !l.serialId && l.row.id === line.row.id)
            .reduce((s, l) => s + l.qty, 0);
          cap = line.row.quantityOnHand - (totalPlain - line.qty);
        }
        const nextQty = Math.min(n, cap);
        if (nextQty < 1) {
          toast.error("ສະຕ໋ອກບໍ່ພໍ");
          return prev;
        }
        return prev.map((l) =>
          posCartLineKey(l) === lineKey ? { ...l, qty: nextQty } : l,
        );
      });
    },
    [setCart],
  );

  const splitCartLineAt = useCallback(
    (lk: string) => {
      setCart((prev) => {
        const idx = prev.findIndex((l) => posCartLineKey(l) === lk);
        if (idx < 0) return prev;
        const line = prev[idx];
        if (!line) return prev;
        if (line.serialId) {
          toast.error("ບໍ່ສາມາດແຍກ serial ລາຍການ (ຈຳນວນ 1)");
          return prev;
        }
        if (line.qty < 2) return prev;
        const rest = line.qty - 1;
        const line1: ShopPosCartLineExt = { ...line, qty: 1 };
        const line2: ShopPosCartLineExt = {
          ...line,
          lineInstanceId: newPosLineInstanceId(),
          qty: rest,
          lineDiscountAmount: undefined,
          lineDiscountPercent: undefined,
          lineDiscountUsePercent: false,
        };
        const next = [...prev];
        next[idx] = line1;
        next.splice(idx + 1, 0, line2);
        return next;
      });
    },
    [setCart],
  );

  const decrementQty = useCallback(
    (lk: string) => {
      setCart((prev) =>
        prev
          .map((l) => (posCartLineKey(l) === lk ? { ...l, qty: l.qty - 1 } : l))
          .filter((l) => l.qty > 0),
      );
    },
    [setCart],
  );

  const incrementQty = useCallback(
    (lk: string) => {
      setCart((prev) => {
        const line = prev.find((l) => posCartLineKey(l) === lk);
        if (!line || line.serialId != null) return prev;
        const cur = line.qty;
        const nextQty = cur + 1;
        const r = line.row;
        if (line.lotId != null && line.lotQtyCap != null) {
          const totalSameLot = prev
            .filter((l) => l.row.id === line.row.id && l.lotId === line.lotId)
            .reduce((s, l) => s + l.qty, 0);
          if (totalSameLot - cur + nextQty > line.lotQtyCap) {
            toast.error("ສະຕ໋ອກບໍ່ພໍ");
            return prev;
          }
        } else if (r.quantityOnHand !== null) {
          const totalPlain = prev
            .filter((l) => !l.lotId && !l.serialId && l.row.id === r.id)
            .reduce((s, l) => s + l.qty, 0);
          if (totalPlain - cur + nextQty > r.quantityOnHand) {
            toast.error("ສະຕ໋ອກບໍ່ພໍ");
            return prev;
          }
        }
        return prev.map((l) =>
          posCartLineKey(l) === lk ? { ...l, qty: l.qty + 1 } : l,
        );
      });
    },
    [setCart],
  );

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 border-border border-b bg-muted px-3 py-2.5 font-semibold text-[10px] text-muted-foreground uppercase tracking-wider sm:px-4 sm:text-xs">
        <span>ລາຍລະອຽດສິນຄ້າ</span>
        <span className="hidden text-end sm:block">ລາຄາ/ຫົວໜ່ວຍ</span>
        <span className="text-center">ຈຳນວນ</span>
        <span className="text-end">ລວມ</span>
      </div>

      <div className="max-h-[min(52vh,28rem)] overflow-y-auto">
        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
            <div className="flex size-20 items-center justify-center rounded-2xl bg-muted">
              <ScanBarcodeIcon className="size-10 text-primary" aria-hidden />
            </div>
            <div>
              <p className="font-bold font-heading text-foreground text-lg">
                ຍັງບໍ່ມີສິນຄ້າໃນກະຕ່າ
              </p>
              <p className="mt-2 max-w-sm text-muted-foreground text-sm leading-relaxed">
                {simpleMode ? (
                  <>
                    ສະແກນດ້ານເທິງ ຫຼື ເປີດ{" "}
                    <span className="font-semibold text-foreground">
                      «ເລືອກສິນຄ້າ»
                    </span>{" "}
                    ດ້ານລຸ່ມ
                  </>
                ) : (
                  <>ສະແກນ barcode ຮ້ານນີ້ເພື່ອເລີ່ມ — ຫຼື ເລືອກຈາກໝວດ ຂ້າງເທິງ</>
                )}
              </p>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {cart.map((line) => {
              const gross = lineGrossTotal(line);
              const disc = lineDiscountCapped(line);
              const lineNet = gross - disc;
              const lk = posCartLineKey(line);
              const isLastScanned = lk === lastScannedLineKey;
              return (
                <li
                  key={lk}
                  className={cn(
                    "px-3 py-4 sm:px-4",
                    simpleMode
                      ? "flex flex-col gap-3"
                      : "grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto_auto] sm:items-center sm:gap-2",
                  )}
                >
                  <div className="flex min-w-0 gap-3">
                    <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-muted">
                      {(() => {
                        const src = line.row.imageUrl
                          ? resolveImageSrc(line.row.imageUrl)
                          : null;
                        return src ? (
                          <img
                            src={src}
                            alt=""
                            className="size-full rounded-lg object-cover"
                          />
                        ) : (
                          <PackageIcon className="size-6 text-secondary" />
                        );
                      })()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-bold font-heading text-card-foreground text-sm leading-tight">
                          {line.row.productName}
                        </p>
                        {isLastScanned ? (
                          <span className="rounded bg-secondary px-1.5 py-0.5 font-semibold text-[10px] text-secondary-foreground uppercase tracking-wide">
                            ສະແກນລ່າສຸດ
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-0.5 truncate font-mono text-muted-foreground text-xs">
                        SKU: {line.row.shopSku}
                        {line.row.barcode ? (
                          <span> · {line.row.barcode}</span>
                        ) : null}
                      </p>
                      {line.row.categoryName ? (
                        <p className="mt-0.5 truncate text-muted-foreground text-xs">
                          ໝວດ: {line.row.categoryName}
                        </p>
                      ) : null}
                      {line.lotSummary ? (
                        <p className="mt-1 font-medium text-secondary text-xs">
                          Lot: {line.lotSummary}
                        </p>
                      ) : null}
                      {line.serialLabel ? (
                        <p className="mt-1 font-medium text-secondary text-xs">
                          Serial: {line.serialLabel}
                        </p>
                      ) : null}
                      {shopId && branchId ? (
                        <PosCartLocationHint
                          shopId={shopId}
                          branchId={branchId}
                          shopProductSkuId={line.row.shopProductSkuId}
                          featureFlags={featureFlags}
                        />
                      ) : null}
                      {!simpleMode ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <label
                            htmlFor={`disc-${lk}`}
                            className="text-muted-foreground text-xs"
                          >
                            ຫຼຸດລາຍການ
                          </label>
                          <Button
                            type="button"
                            size="sm"
                            variant={
                              line.lineDiscountUsePercent !== true
                                ? "default"
                                : "outline"
                            }
                            className="h-8 px-2 text-[11px]"
                            onClick={() =>
                              setCart((p) =>
                                p.map((l) => {
                                  if (posCartLineKey(l) !== lk) return l;
                                  const disc = lineDiscountCapped(l);
                                  return {
                                    ...l,
                                    lineDiscountUsePercent: false,
                                    lineDiscountPercent: undefined,
                                    lineDiscountAmount:
                                      disc > 0 ? disc : undefined,
                                  };
                                }),
                              )
                            }
                          >
                            ເງິນ
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={
                              line.lineDiscountUsePercent === true
                                ? "default"
                                : "outline"
                            }
                            className="h-8 px-2 text-[11px]"
                            onClick={() =>
                              setCart((p) =>
                                p.map((l) => {
                                  if (posCartLineKey(l) !== lk) return l;
                                  const g = lineGrossTotal(l);
                                  const amt = l.lineDiscountAmount ?? 0;
                                  const nextPct =
                                    amt > 0 && g > 0
                                      ? Math.min((amt / g) * 100, 100)
                                      : 0;
                                  return {
                                    ...l,
                                    lineDiscountUsePercent: true,
                                    lineDiscountPercent: nextPct,
                                    lineDiscountAmount: undefined,
                                  };
                                }),
                              )
                            }
                          >
                            %
                          </Button>
                          {line.lineDiscountUsePercent === true ? (
                            <Input
                              id={`disc-${lk}`}
                              type="number"
                              min={0}
                              max={100}
                              step={0.01}
                              className="h-8 w-20 font-mono text-xs"
                              value={line.lineDiscountPercent ?? ""}
                              placeholder="0"
                              onChange={(e) => {
                                const raw = e.target.value;
                                const n =
                                  raw === "" ? 0 : Number.parseFloat(raw);
                                const v = Number.isFinite(n)
                                  ? Math.min(Math.max(0, n), 100)
                                  : 0;
                                setCart((p) =>
                                  p.map((l) =>
                                    posCartLineKey(l) === lk
                                      ? {
                                          ...l,
                                          lineDiscountUsePercent: true,
                                          lineDiscountPercent: v,
                                        }
                                      : l,
                                  ),
                                );
                              }}
                            />
                          ) : (
                            <Input
                              id={`disc-${lk}`}
                              type="number"
                              min={0}
                              step={1}
                              className="h-8 w-24 font-mono text-xs"
                              value={
                                line.lineDiscountAmount != null &&
                                line.lineDiscountAmount > 0
                                  ? line.lineDiscountAmount
                                  : ""
                              }
                              placeholder="0"
                              onChange={(e) => {
                                const raw = e.target.value;
                                const n =
                                  raw === "" ? 0 : Number.parseFloat(raw);
                                setCart((p) =>
                                  p.map((l) =>
                                    posCartLineKey(l) === lk
                                      ? {
                                          ...l,
                                          lineDiscountAmount:
                                            Number.isFinite(n) && n > 0
                                              ? n
                                              : undefined,
                                        }
                                      : l,
                                  ),
                                );
                              }}
                            />
                          )}
                        </div>
                      ) : null}
                      {line.qty >= 2 && !line.serialId && !simpleMode ? (
                        <div className="mt-1.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1 px-2 text-[11px] text-secondary hover:text-secondary"
                            title="ແຍກ 1 ຕົວອອກ — ສ່ວນຫຼຸດຕໍ່ລາຍການຢູ່ບັນທັດເທິງເທົ່ານັ້ນ"
                            onClick={() => splitCartLineAt(lk)}
                          >
                            <Scissors
                              className="size-3.5 shrink-0"
                              aria-hidden
                            />
                            ແຍກ (1 ຕົວ)
                          </Button>
                        </div>
                      ) : null}
                      <p className="mt-1 text-muted-foreground text-xs sm:hidden">
                        {formatPrice(line.row.price, line.row.currency)} ×{" "}
                        {line.qty}
                      </p>
                    </div>
                  </div>
                  <div
                    className={
                      simpleMode
                        ? "flex items-center justify-between gap-3"
                        : "contents"
                    }
                  >
                    <p
                      className={cn(
                        "text-end font-medium text-card-foreground tabular-nums",
                        simpleMode
                          ? "block text-lg"
                          : "hidden text-sm sm:block",
                      )}
                    >
                      {formatPrice(line.row.price, line.row.currency)}
                    </p>
                    <div className="flex items-center justify-center gap-2 justify-self-center sm:justify-self-auto">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className={cn(
                          "shrink-0 touch-manipulation border-border",
                          simpleMode
                            ? "size-10 rounded-lg"
                            : "size-10 rounded-lg",
                        )}
                        onClick={() => decrementQty(lk)}
                        aria-label="ຫຼຸດຈຳນວນ"
                      >
                        <MinusIcon
                          className="size-5"
                          strokeWidth={2.5}
                        />
                      </Button>
                      {qtyEditLineKey === lk ? (
                        <Input
                          type="number"
                          min={1}
                          inputMode="numeric"
                          autoFocus
                          defaultValue={line.qty}
                          className={cn(
                            "text-center font-bold tabular-nums",
                            simpleMode
                              ? "h-10 w-14 text-lg"
                              : "h-10 w-14 text-lg",
                          )}
                          onBlur={(e) => commitQtyEdit(lk, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              commitQtyEdit(lk, e.currentTarget.value);
                            }
                            if (e.key === "Escape") {
                              setQtyEditLineKey(null);
                            }
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          className={cn(
                            "rounded-lg text-center font-bold tabular-nums touch-manipulation",
                            simpleMode
                              ? "min-h-10 min-w-12 px-1 text-lg"
                              : "min-h-10 min-w-14 px-1 text-lg",
                          )}
                          title={simpleMode ? "ແຕະສອງຄັ້ງເພື່ອແກ້ຈຳນວນ" : undefined}
                          onClick={() => handleQtyTap(lk)}
                        >
                          {line.qty}
                        </button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className={cn(
                          "shrink-0 touch-manipulation border-border",
                          simpleMode
                            ? "size-10 rounded-lg"
                            : "size-10 rounded-lg",
                        )}
                        disabled={line.serialId != null || !posSalesUnlocked}
                        title={
                          line.serialId != null
                            ? "ເພີ່ມ serial ອື່ນດ້ວຍການສະແກນ ຫຼື ເລືອກຈາກລາຍການ"
                            : !posSalesUnlocked
                              ? "ຕ້ອງເປີດກະກ່ອນ"
                              : undefined
                        }
                        onClick={() => incrementQty(lk)}
                        aria-label="ເພີ່ມຈຳນວນ"
                      >
                        <PlusIcon
                          className="size-5"
                          strokeWidth={2.5}
                        />
                      </Button>
                    </div>
                    <div className="text-end">
                      {disc > 0 ? (
                        <p className="text-[10px] text-muted-foreground tabular-nums line-through">
                          {formatPrice(gross, line.row.currency)}
                        </p>
                      ) : null}
                      <p
                        className={cn(
                          "font-heading font-semibold text-card-foreground tabular-nums",
                          simpleMode ? "text-xl" : "text-sm",
                        )}
                      >
                        {formatPrice(lineNet, line.row.currency)}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div
        className="relative overflow-hidden border-border border-t bg-muted/40 px-4 py-6"
        style={{
          backgroundImage:
            "linear-gradient(135deg, color-mix(in oklab, var(--foreground) 6%, transparent) 0%, transparent 50%)",
        }}
      >
        <p
          className="pointer-events-none select-none font-bold font-heading text-[clamp(2rem,6vw,3.5rem)] text-foreground/[0.07] uppercase leading-none"
          aria-hidden
        >
          PARTZYNC
        </p>
      </div>
    </div>
  );
}
